/**
 * One-at-a-time sequencing for the editor's saves (MDRS-554 / audit RB-8).
 *
 * `runSave` performs TWO versioned writes per call — the module payload, then
 * the .docx — and carries the version token the first produced into the
 * second. Two overlapping calls therefore read the same token and the loser is
 * refused by its own sibling: a single tab conflicting with itself, reported
 * to the user as "another tab — or another person" edited their report.
 *
 * The previous guard was the `saveDraft` mutation's `isPending`, which is
 * already false during the .docx upload — exactly the window a 1500ms autosave
 * tick lands in.
 *
 * Chaining rather than dropping, because the callers differ: an autosave tick
 * could safely be skipped, but an explicit Save or a pre-finalise save must
 * actually run (MDRS-545 gates finalise on the result). Each waits for the one
 * before it and then reads the token its predecessor advanced.
 */

/**
 * Returns an `enqueue` that runs tasks strictly in submission order, never
 * concurrently.
 *
 * `seed` only establishes the chain's starting value; nothing observes it.
 *
 * Hold the returned function in a ref, not a memo: it owns the chain, so
 * recreating it when some dependency changes identity would let a new chain
 * run alongside the old one — which is the overlap it exists to prevent.
 */
export const createSaveChain = <R>(seed: R) => {
  let chain: Promise<unknown> = Promise.resolve(seed);

  return <T extends R>(task: () => Promise<T>): Promise<T> => {
    // `.catch` BEFORE `.then`: a rejected save must not wedge every save after
    // it (a dropped connection on one tick would otherwise take out the
    // pre-finalise save too). The rejection is still delivered to that task's
    // own caller, because that caller holds `next`, not the swallowed branch.
    const next = chain.catch(() => undefined).then(task);
    chain = next;
    return next;
  };
};
